import { Controller } from '@hotwired/stimulus';
import { subscribeTo } from '../channels/marketing_team_channel';

export default class extends Controller {
  static targets = ['feed', 'input', 'reviewPanel'];
  static values = { teamId: Number };

  connect() {
    if (this.teamIdValue) {
      subscribeTo(this.teamIdValue);
    }

    if (this.hasFeedTarget) {
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    // Enter key sends chat (same pattern as Sales Team)
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.sendChat();
        }
      });
    }

    // Listen for team status broadcasts
    this._statusHandler = (event) => this.handleTeamStatus(event.detail);
    document.addEventListener('marketing-team:status', this._statusHandler);

    // Listen for assets becoming ready (image generated / copy written) and
    // upsert their card into the review panel live, no refresh needed.
    this._assetHandler = (event) => this.handleAssetReady(event.detail);
    document.addEventListener('marketing-team:asset', this._assetHandler);
  }

  disconnect() {
    if (this._statusHandler) {
      document.removeEventListener('marketing-team:status', this._statusHandler);
    }
    if (this._assetHandler) {
      document.removeEventListener('marketing-team:asset', this._assetHandler);
    }
    if (this._workingTimeout) {
      clearTimeout(this._workingTimeout);
    }
  }

  // ── Status Handling ───────────────────────────────────

  handleTeamStatus(data) {
    if (data.status === 'working') {
      this.showWorkingIndicator();
    } else if (data.status === 'done') {
      this.hideWorkingIndicator();
    }
  }

  showWorkingIndicator() {
    this.hideWorkingIndicator();

    const indicator = document.createElement('div');
    indicator.id = 'marketing-team-working';
    indicator.classList.add('activity-item', 'activity-working');
    indicator.innerHTML = `
      <div class="activity-content">
        <span class="working-dots"><span></span><span></span><span></span></span>
        Team is working&hellip;
      </div>
    `;
    if (this.hasFeedTarget) {
      this.feedTarget.appendChild(indicator);
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    this.setInputWorking(true);
    this._workingTimeout = setTimeout(() => this.hideWorkingIndicator(), 300000);
  }

  hideWorkingIndicator() {
    const indicator = document.getElementById('marketing-team-working');
    if (indicator) indicator.remove();

    this.setInputWorking(false);

    if (this._workingTimeout) {
      clearTimeout(this._workingTimeout);
      this._workingTimeout = null;
    }
  }

  setInputWorking(working) {
    if (this.hasInputTarget) {
      this.inputTarget.placeholder = working
        ? 'Team is working... send another instruction anytime'
        : 'Tell your marketing team what to create...';
    }
  }

  // ── Chat ──────────────────────────────────────────────

  sendChat() {
    const message = this.inputTarget.value.trim();
    if (message === '') return;

    // Show owner message in feed immediately (optimistic)
    const feedItem = document.createElement('div');
    feedItem.classList.add('activity-item', 'activity-owner');
    feedItem.innerHTML = `
      <div class="activity-header">
        <span class="activity-role activity-role-owner">You</span>
        <span class="activity-time">just now</span>
      </div>
      <div class="activity-content">${this.escapeHtml(message)}</div>
    `;
    this.feedTarget.appendChild(feedItem);
    this.feedTarget.scrollTop = this.feedTarget.scrollHeight;

    this.inputTarget.value = '';
    this.showWorkingIndicator();

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    fetch('/marketing_team/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ message })
    })
    .then(r => r.json())
    .then(data => {
      if (data.error) {
        this.showFeedError(data.error);
        this.hideWorkingIndicator();
      }
    })
    .catch(err => {
      this.showFeedError('Failed to send message');
      this.hideWorkingIndicator();
      console.error('Chat error:', err);
    });
  }

  // ── Live Asset Updates ────────────────────────────────

  async handleAssetReady(data) {
    if (!data || !data.asset_id) return;

    try {
      const res = await fetch(`/marketing_team/asset_card/${data.asset_id}`, {
        headers: { Accept: 'text/html' }
      });
      if (!res.ok) return;

      const html = (await res.text()).trim();
      if (!html) return;
      this.upsertAssetCard(data.asset_id, html);
    } catch (err) {
      console.error('Asset update failed:', err);
    }
  }

  upsertAssetCard(assetId, html) {
    const container = document.getElementById('marketing-asset-cards');
    if (!container) return;

    // Clear the "No content pending review" empty state on first asset.
    const empty = container.querySelector('.review-empty');
    if (empty) empty.remove();

    const template = document.createElement('div');
    template.innerHTML = html;
    const card = template.firstElementChild;
    if (!card) return;

    const existing = document.getElementById(`asset-card-${assetId}`);
    if (existing) {
      existing.replaceWith(card);
    } else {
      container.insertBefore(card, container.firstChild);
    }

    this.flashCard(card);
    this.updateAssetCount();
  }

  flashCard(card) {
    card.classList.add('email-card-approved');
    setTimeout(() => card.classList.remove('email-card-approved'), 1200);
  }

  updateAssetCount() {
    const badge = document.getElementById('asset-count-badge');
    if (!badge) return;

    const count = document.querySelectorAll('#marketing-asset-cards .email-card').length;
    badge.textContent = count;
    badge.style.display = count > 0 ? '' : 'none';
  }

  // ── Asset Editing ─────────────────────────────────────

  publishAsset(event) {
    event.preventDefault();
    const assetId = event.currentTarget.dataset.assetId;
    const btn = event.currentTarget;
    const label = btn.textContent.trim(); // "Post to Facebook" / "Post to Instagram"
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!confirm(`${label} now?`)) return;

    btn.disabled = true;
    btn.textContent = 'Posting…';

    fetch('/marketing_team/publish_asset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ asset_id: assetId })
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'posted') {
          // Re-render the card (now "Posted") with the View link.
          this.handleAssetReady({ asset_id: assetId });
        } else {
          btn.disabled = false;
          btn.textContent = label;
          this.showFeedError(data.error || 'Could not publish the post');
        }
      })
      .catch((err) => {
        btn.disabled = false;
        btn.textContent = label;
        console.error('Publish error:', err);
        this.showFeedError('Could not publish the post');
      });
  }

  editAsset(event) {
    event.preventDefault();
    const card = document.getElementById(`asset-card-${event.currentTarget.dataset.assetId}`);
    if (!card) return;
    // Hide the rendered body/actions, reveal the edit form.
    card.querySelectorAll('.email-card-body, .asset-image-preview, .asset-hashtags, .email-card-actions')
      .forEach((el) => { el.style.display = 'none'; });
    const form = card.querySelector('[data-asset-edit-form]');
    if (form) form.style.display = 'block';
  }

  cancelEditAsset(event) {
    event.preventDefault();
    // Re-render the card from the server to discard edits cleanly.
    this.handleAssetReady({ asset_id: event.currentTarget.dataset.assetId });
  }

  saveAsset(event) {
    event.preventDefault();
    const assetId = event.currentTarget.dataset.assetId;
    const card = document.getElementById(`asset-card-${assetId}`);
    if (!card) return;

    const payload = { asset_id: assetId };
    card.querySelectorAll('[data-asset-edit-field]').forEach((el) => {
      payload[el.dataset.assetEditField] = el.value;
    });

    const btn = event.currentTarget;
    btn.disabled = true;
    btn.textContent = 'Saving…';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    fetch('/marketing_team/update_asset', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(payload)
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'updated') {
          // Re-render the card with the saved content.
          this.handleAssetReady({ asset_id: assetId });
        } else {
          btn.disabled = false;
          btn.textContent = 'Save';
          this.showFeedError(data.error || 'Could not save changes');
        }
      })
      .catch((err) => {
        btn.disabled = false;
        btn.textContent = 'Save';
        console.error('Save asset error:', err);
        this.showFeedError('Could not save changes');
      });
  }

  // ── Asset Actions ─────────────────────────────────────

  approveAsset(event) {
    event.preventDefault();
    const assetId = event.currentTarget.dataset.assetId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const card = document.getElementById(`asset-card-${assetId}`);

    fetch('/marketing_team/approve_asset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ asset_id: assetId })
    })
    .then(r => r.json())
    .then(data => {
      if (data.status === 'sent' || data.status === 'approved') {
        if (card) {
          card.classList.add('email-card-approved');
          const actions = card.querySelector('.email-card-actions');
          if (actions) {
            actions.innerHTML = `<span class="label label-success">${data.status === 'sent' ? 'Sent' : 'Approved'}</span>`;
          }
          setTimeout(() => card.classList.remove('email-card-approved'), 2000);
        }
      }
    })
    .catch(err => console.error('Approve error:', err));
  }

  rejectAsset(event) {
    event.preventDefault();
    const assetId = event.currentTarget.dataset.assetId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const card = document.getElementById(`asset-card-${assetId}`);

    fetch('/marketing_team/reject_asset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ asset_id: assetId })
    })
    .then(r => r.json())
    .then(() => {
      if (card) {
        card.classList.add('email-card-rejected');
        setTimeout(() => card.remove(), 500);
      }
    })
    .catch(err => console.error('Reject error:', err));
  }

  copyContent(event) {
    event.preventDefault();
    const content = event.currentTarget.dataset.content;
    navigator.clipboard.writeText(content).then(() => {
      const btn = event.currentTarget;
      const original = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 1500);
    });
  }

  expandNewsletter(event) {
    event.preventDefault();
    const assetId = event.currentTarget.dataset.assetId;
    const src = `/marketing_team/preview/${assetId}`;
    const win = window.open(src, '_blank', 'width=700,height=900');
    if (win) win.focus();
  }

  // ── Utilities ─────────────────────────────────────────

  refreshFeed(event) {
    event.preventDefault();
    window.location.reload();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showFeedError(message) {
    const item = document.createElement('div');
    item.classList.add('activity-item', 'activity-error');
    item.innerHTML = `<div class="activity-content" style="color:#a94442">${this.escapeHtml(message)}</div>`;
    if (this.hasFeedTarget) {
      this.feedTarget.appendChild(item);
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }
  }
}
